import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Wordmark, T, F } from "@/lib/bricks";
import { Button } from "@/components/developer/siteUi";
import { humanDate } from "@/components/developer/siteTimelineModel";
import { localDay } from "@/lib/localDay";
import { money } from "@/pages/developer/propertyOps";

// Printable property reports: condition (room by room against the last one),
// owner, leasing package, baseline against latest, insurance claim pack.
// Paper is always light. "Print or save PDF" prints only the paper.
// Theme-following sheet (index.css vars); printing always resolves to paper.
const P = { bg: "var(--c-surf)", ink: "var(--c-ink)", ink2: "var(--c-ink-soft)", mute: "var(--c-ink-mute)", hair: "var(--c-hair-s)" };
const CSS = `
.or-root{position:fixed;inset:0;z-index:2000;overflow-y:auto;background:rgba(20,18,16,0.72);-webkit-overflow-scrolling:touch}
.or-bar{position:sticky;top:0;z-index:1;display:flex;justify-content:flex-end;gap:8px;padding:12px 16px;background:${T.night}}
.or-paper{max-width:860px;margin:24px auto 64px;padding:56px 64px;box-sizing:border-box;background:${P.bg};color:${P.ink};font-family:${F.display}}
.or-facts{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px 24px}
.or-pair{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.or-photos{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}
.or-sec{margin-top:36px;break-inside:avoid;page-break-inside:avoid}
.or-table{width:100%;border-collapse:collapse;font-size:13.5px}
.or-table th{text-align:left;font-weight:500;color:${P.mute};font-size:12.5px;padding:8px 8px 8px 0;border-bottom:1px solid ${P.hair}}
.or-table td{padding:10px 8px 10px 0;border-bottom:1px solid ${P.hair};vertical-align:top}
@media (max-width:719px){.or-root{background:var(--c-surf)}.or-paper{margin:0;padding:32px 20px 48px}.or-facts{grid-template-columns:repeat(2,minmax(0,1fr))}.or-photos{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media print{@page{margin:14mm}html,body{background:#fff !important}body>*:not(.or-root){display:none !important}
.or-root{position:static !important;overflow:visible !important;background:#fff !important}.or-bar{display:none !important}
.or-paper{max-width:none;margin:0;padding:0}.or-facts{grid-template-columns:repeat(4,minmax(0,1fr))}.or-photos{grid-template-columns:repeat(3,minmax(0,1fr))}.or-paper a{color:${P.ink};text-decoration:none}}
`;
const shot = { display: "block", width: "100%", aspectRatio: "4 / 3", objectFit: "cover", background: "var(--c-bg-soft)" };
const NAMES = { condition: "Condition report", owner: "Owner report", leasing: "Leasing package", baseline: "Baseline and latest", claim: "Insurance claim pack" };
const MOVE = { move_in: "Move-in", move_out: "Move-out" };
const ROOM_STATE = { both: "", only_latest: "New in this report", only_previous: "Not photographed this time" };

const H = ({ children }) => <h2 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 500, color: P.ink }}>{children}</h2>;
const Fact = ({ label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: 12.5, color: P.mute }}>{label}</div>
    <div style={{ fontSize: 15, marginTop: 4, overflowWrap: "anywhere" }}>{value || "None"}</div>
  </div>
);
const Muted = ({ children }) => <p style={{ margin: 0, fontSize: 14, color: P.mute }}>{children}</p>;
const visitLine = (v) => (v ? [humanDate(v.date), v.service_label, MOVE[v.move_kind]].filter(Boolean).join(" · ") : "None on file");
const Img = ({ f, alt }) => (f ? <a href={f.url} target="_blank" rel="noreferrer"><img src={f.preview_url || f.url} alt={alt} style={shot} /></a> : <div style={{ ...shot, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, color: P.mute }}>No photo</div>);

export function reportParams(view, q = {}) {
  const out = { view };
  if (view === "condition" && q.unit) out.unit = q.unit;
  if (view === "claim" && q.loss_date) out.loss_date = q.loss_date;
  if (view === "owner") { if (q.date_from) out.date_from = q.date_from; if (q.date_to) out.date_to = q.date_to; }
  return out;
}

function Condition({ d }) {
  return (
    <>
      <div className="or-facts or-sec">
        <Fact label="Unit" value={d.unit} />
        <Fact label="This report" value={visitLine(d.latest)} />
        <Fact label="Compared with" value={visitLine(d.previous)} />
        <Fact label="Rooms" value={String((d.rooms || []).length)} />
      </div>
      {d.note && <section className="or-sec"><Muted>{d.note}</Muted></section>}
      {(d.rooms || []).map((r) => (
        <section key={r.room} className="or-sec" data-testid="or-room">
          <H>{r.room}{ROOM_STATE[r.state] ? <span style={{ fontWeight: 400, color: P.mute }}>{` · ${ROOM_STATE[r.state]}`}</span> : null}</H>
          <div className={d.previous ? "or-pair" : "or-photos"}>
            {d.previous && <figure style={{ margin: 0 }}><Img f={(r.previous || [])[0]} alt={`${r.room}, ${d.previous.date}`} /><figcaption style={{ marginTop: 6, fontSize: 12.5, color: P.mute }}>{humanDate(d.previous.date)}</figcaption></figure>}
            {(d.previous ? [(r.latest || [])[0]] : r.latest || []).map((f, i) => (
              <figure key={i} style={{ margin: 0 }}><Img f={f} alt={`${r.room}, ${d.latest && d.latest.date}`} /><figcaption style={{ marginTop: 6, fontSize: 12.5, color: P.mute }}>{d.latest ? humanDate(d.latest.date) : ""}</figcaption></figure>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function Owner({ d }) {
  const u = d.units || {};
  return (
    <>
      <div className="or-facts or-sec">
        <Fact label="Period" value={`${humanDate(d.period.from)} to ${humanDate(d.period.to)}`} />
        <Fact label="Units" value={String(u.total || 0)} />
        <Fact label="Need a visit" value={String(u.needs_action || 0)} />
        <Fact label="Spent, before tax" value={money(d.spend_cad_cents || 0)} />
      </div>
      <section className="or-sec"><H>Units</H>
        <table className="or-table"><thead><tr><th>Unit</th><th>Status</th><th>Last condition report</th><th>Next step</th></tr></thead>
          <tbody>{(d.unit_rows || []).map((r) => <tr key={r.unit}><td>{r.unit}</td><td>{r.status_label}</td><td>{r.last_condition ? humanDate(r.last_condition) : "None"}</td><td>{r.next_step}</td></tr>)}</tbody></table>
      </section>
      <section className="or-sec"><H>Visits this period</H>
        {(d.visits || []).length ? (
          <table className="or-table"><thead><tr><th>Date</th><th>Service</th><th>Unit</th><th>Photos</th></tr></thead>
            <tbody>{d.visits.map((v, i) => <tr key={i}><td>{humanDate(v.date)}</td><td>{[v.service_label, MOVE[v.move_kind]].filter(Boolean).join(", ")}</td><td>{v.unit || "Building"}</td><td>{v.photos}</td></tr>)}</tbody></table>
        ) : <Muted>No visits in this period.</Muted>}
      </section>
    </>
  );
}

function Leasing({ d }) {
  return (
    <>
      <div className="or-facts or-sec">
        <Fact label="Spaces" value={String((d.spaces || []).length)} />
        <Fact label="Available" value={`${(d.available_sqft || 0).toLocaleString("en-CA")} sq ft`} />
        <Fact label="Aerial" value={d.aerial ? humanDate(d.aerial.date) : "None yet"} />
        <Fact label="Address" value={d.property && d.property.address} />
      </div>
      {(d.spaces || []).map((x) => (
        <section key={x.suite} className="or-sec" data-testid="or-space">
          <H>{`Suite ${x.suite}`}<span style={{ fontWeight: 400, color: P.mute }}>{` · ${(x.sqft || 0).toLocaleString("en-CA")} sq ft · ${x.status_label}${x.asking_cents ? ` · ${money(x.asking_cents)}/sq ft/yr` : ""}`}</span></H>
          {x.photos && x.photos.length ? <div className="or-photos">{x.photos.slice(0, 6).map((u) => <img key={u} src={u} alt={`Suite ${x.suite}`} style={shot} />)}</div> : <Muted>No leasing photos yet.</Muted>}
          <p style={{ margin: "10px 0 0", fontSize: 13, color: P.ink2 }}>
            {[x.floor_plan_url && <a key="fp" href={x.floor_plan_url}>Floor plan</a>, x.virtual_visit_url && <a key="vv" href={x.virtual_visit_url}>Virtual Visit</a>, x.video_url && <a key="vd" href={x.video_url}>Video</a>]
              .filter(Boolean).reduce((a, el, i) => (i ? [...a, " · ", el] : [el]), [])}
          </p>
        </section>
      ))}
      {(d.aerial_photos || []).length ? <section className="or-sec"><H>Aerial</H><div className="or-photos">{d.aerial_photos.slice(0, 3).map((u) => <img key={u} src={u} alt="Aerial" style={shot} />)}</div></section> : null}
    </>
  );
}

function Baseline({ d }) {
  return (
    <>
      <div className="or-facts or-sec">
        <Fact label="Baseline" value={visitLine(d.baseline)} />
        <Fact label="Latest" value={visitLine(d.latest)} />
        <Fact label="Days between" value={d.days_between != null ? String(d.days_between) : "None"} />
      </div>
      {d.note && <section className="or-sec"><Muted>{d.note}</Muted></section>}
      {(d.pairs || []).map((x, i) => (
        <section key={i} className="or-sec" data-testid="or-pair"><H>{x.kind_label}</H>
          <div className="or-pair">
            <figure style={{ margin: 0 }}><Img f={x.baseline} alt={`${x.kind_label}, baseline`} /><figcaption style={{ marginTop: 6, fontSize: 12.5, color: P.mute }}>{`Baseline · ${humanDate(d.baseline.date)}`}</figcaption></figure>
            <figure style={{ margin: 0 }}><Img f={x.latest} alt={`${x.kind_label}, latest`} /><figcaption style={{ marginTop: 6, fontSize: 12.5, color: P.mute }}>{`Latest · ${d.latest ? humanDate(d.latest.date) : ""}`}</figcaption></figure>
          </div>
        </section>
      ))}
    </>
  );
}

function Claim({ d }) {
  const side = (v, title, when) => (
    <section className="or-sec" style={{ breakInside: "auto" }}><H>{title}</H>
      {v ? (<><Muted>{`${visitLine(v)} · ${when}`}</Muted>
        <div className="or-photos" style={{ marginTop: 12 }}>{(v.files || []).filter((f) => f.preview_url).slice(0, 9).map((f, i) => <Img key={i} f={f} alt={`${f.label || f.kind_label}, ${f.captured_on || v.date}`} />)}</div></>)
        : <Muted>No delivered visit on file.</Muted>}
    </section>
  );
  return (
    <>
      <div className="or-facts or-sec">
        <Fact label="Date of loss" value={humanDate(d.loss_date)} />
        <Fact label="Before" value={d.before ? humanDate(d.before.date) : "None"} />
        <Fact label="After" value={d.after ? humanDate(d.after.date) : "None"} />
      </div>
      {d.note && <section className="or-sec"><Muted>{d.note}</Muted></section>}
      {side(d.before, "Before the loss", d.before ? `${d.before.days_before} days before` : "")}
      {side(d.after, "After the loss", d.after ? `${d.after.days_after} days after` : "")}
      <section className="or-sec"><Muted>Photos are dated by capture. Originals stay in your Bricks record for your insurer or adjuster.</Muted></section>
    </>
  );
}

export default function OpsReport({ projectId, view, query = {}, property = {}, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let live = true;
    setD(null); setErr("");
    api.get(`/dev/projects/${projectId}/ops`, { params: reportParams(view, query) })
      .then((r) => { if (live) setD(r.data); })
      .catch((e) => { if (live) setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn’t load this report."); });
    return () => { live = false; };
  }, [projectId, view, query.unit, query.loss_date, query.date_from, query.date_to]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow, title = document.title;
    document.body.style.overflow = "hidden";
    document.title = `${NAMES[view] || "Report"} · ${property.name || "Bricks"}`;
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; document.title = title; };
  }, [onClose, view, property.name]);

  const Body = { condition: Condition, owner: Owner, leasing: Leasing, baseline: Baseline, claim: Claim }[view];
  return createPortal(
    <div className="or-root" role="dialog" aria-modal="true" aria-label={NAMES[view]} data-testid="ops-report">
      <style>{CSS}</style>
      <div className="or-bar">
        <Button tone="dark" icon="download" onClick={() => window.print()} data-testid="ops-report-print">Print or save PDF</Button>
        <Button tone="dark" variant="secondary" icon="close" onClick={onClose} aria-label="Close report">Close</Button>
      </div>
      <article className="or-paper">
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, paddingBottom: 24, borderBottom: `1px solid ${P.hair}` }}>
          <Wordmark size={18} color={P.ink} />
          <span style={{ fontSize: 13, color: P.mute }}>{NAMES[view]}</span>
        </header>
        <div style={{ marginTop: 32 }}>
          <h1 style={{ margin: 0, fontSize: 32, lineHeight: 1.1, fontWeight: 500, letterSpacing: "-0.02em", overflowWrap: "anywhere" }}>{property.name || (d && d.property && d.property.name) || ""}</h1>
          {property.address ? <p style={{ margin: "8px 0 0", fontSize: 15, color: P.ink2 }}>{property.address}</p> : null}
        </div>
        {err ? <section className="or-sec"><p role="alert" style={{ color: "var(--c-attn)", fontSize: 14 }}>{err}</p></section>
          : !d ? <section className="or-sec"><Muted>Loading…</Muted></section>
          : Body ? <Body d={d} /> : null}
        <footer style={{ marginTop: 48, paddingTop: 16, borderTop: `1px solid ${P.hair}`, display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 8, fontSize: 12.5, color: P.mute }}>
          <span>Bricks · brxs.ca</span>
          <span>{`Prepared ${humanDate(localDay())}`}</span>
        </footer>
      </article>
    </div>,
    document.body,
  );
}
